import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Modal,
    Box,
    IconButton,
    Tooltip,
    Typography,
    TextField,
    CircularProgress,
} from "@mui/material";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

// Use CDN worker so CRA doesn't need to bundle it
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MediaViewer = ({ open, onClose, src, type = "image", onEmail }) => {
    const [rotation, setRotation] = useState(0);

    // PDF state
    const [isPdf, setIsPdf] = useState(false);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [pdfError, setPdfError] = useState("");
    const [useIframeFallback, setUseIframeFallback] = useState(false);

    const canvasRef = useRef(null);
    const canvasWrapRef = useRef(null);

    // Infer PDF by explicit prop or URL extension
    const inferIsPdf = useMemo(() => {
        if (!src) return false;
        if (type === "pdf") return true;
        const u = src.split("?")[0].toLowerCase();
        return u.endsWith(".pdf");
    }, [src, type]);

    useEffect(() => {
        setIsPdf(inferIsPdf);
    }, [inferIsPdf]);

    // Reset when opened or source changes
    useEffect(() => {
        setRotation(0);
        setPdfDoc(null);
        setNumPages(0);
        setPageNumber(1);
        setPdfError("");
        setUseIframeFallback(false);
    }, [open, src]);

    // Load PDF with pdf.js (fallback to iframe on CORS/etc)
    useEffect(() => {
        let cancelled = false;
        if (!open || !isPdf) return;

        const load = async () => {
            setLoadingPdf(true);
            setPdfError("");
            try {
                const task = pdfjsLib.getDocument({ url: src, withCredentials: false });
                const doc = await task.promise;
                if (cancelled) return;
                setPdfDoc(doc);
                setNumPages(doc.numPages);
                setPageNumber(1);
            } catch (e) {
                console.warn("[MediaViewer] pdf.js failed, falling back to iframe:", e && e.message ? e.message : e);
                setUseIframeFallback(true);
                setPdfError("");
            } finally {
                if (!cancelled) setLoadingPdf(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, isPdf, src]);

    const renderPage = useCallback(
        async (pgNum) => {
            if (!pdfDoc || !canvasRef.current || !canvasWrapRef.current) return;
            try {
                const page = await pdfDoc.getPage(pgNum);
                const wrap = canvasWrapRef.current;
                const dpr = window.devicePixelRatio || 1;

                // Fit to container width, with a sensible cap
                const targetCssWidth = Math.min((wrap.clientWidth || 800), 1600);
                const viewport1 = page.getViewport({ scale: 1 });
                const scale = targetCssWidth / viewport1.width;
                const viewport = page.getViewport({ scale });

                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");

                canvas.width = Math.floor(viewport.width * dpr);
                canvas.height = Math.floor(viewport.height * dpr);
                canvas.style.width = `${Math.floor(viewport.width)}px`;
                canvas.style.height = `${Math.floor(viewport.height)}px`;

                const renderContext = {
                    canvasContext: ctx,
                    viewport,
                    transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
                };
                await page.render(renderContext).promise;
            } catch (e) {
                console.error("[MediaViewer] renderPage error:", e && e.message ? e.message : e);
                setPdfError("Failed to render page.");
            }
        },
        [pdfDoc]
    );

    useEffect(() => {
        if (!open || !isPdf || !pdfDoc) return;
        renderPage(pageNumber);
    }, [open, isPdf, pdfDoc, pageNumber, renderPage]);

    // Re-render on resize for responsive fit
    useEffect(() => {
        if (!open || !isPdf || !pdfDoc) return;
        const onResize = () => renderPage(pageNumber);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [open, isPdf, pdfDoc, pageNumber, renderPage]);

    // Actions
    const handleRotateLeft = () => setRotation((prev) => prev - 90);
    const handleRotateRight = () => setRotation((prev) => prev + 90);

    const handleEmail = () => {
        if (onEmail) onEmail(src);
        else window.location.href = `mailto:?subject=Media Attachment&body=Please see the media at ${src}`;
    };

    const handlePrint = () => {
        if (isPdf) {
            const w = window.open(src, "_blank");
            if (w) w.focus();
            return;
        }
        const printWindow = window.open("", "_blank");
        if (!printWindow) return;
        printWindow.document.write(`
      <html>
        <head>
          <title>Print Media</title>
          <style>
            html, body { height:100%; margin:0; }
            body { display:flex; align-items:center; justify-content:center; }
            .media { max-width:100%; max-height:100%; transform: rotate(${rotation}deg); }
          </style>
        </head>
        <body>
          <img class="media" src="${src}" />
        </body>
      </html>
    `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    const tooltipProps = {
        arrow: true,
        componentsProps: {
            tooltip: {
                sx: { bgcolor: "#1976d2", color: "#fff", fontSize: "0.875rem" },
            },
        },
    };

    const onPageInput = (e) => {
        const raw = Number(e.target.value);
        if (Number.isNaN(raw)) return;
        const max = numPages || 1;
        const v = Math.max(1, Math.min(raw, max));
        setPageNumber(v);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
            <Box
                sx={{
                    position: "relative",
                    backgroundColor: "#fff",
                    boxShadow: 24,
                    borderRadius: "8px",
                    width: { xs: "95%", sm: "90%", md: "80%" },
                    maxHeight: "92%",
                    overflow: "hidden",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                {/* Top bar */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip title="Rotate Left" {...tooltipProps}>
                            <IconButton onClick={handleRotateLeft} sx={{ color: "#1976d2" }}>
                                <RotateLeftIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Rotate Right" {...tooltipProps}>
                            <IconButton onClick={handleRotateRight} sx={{ color: "#1976d2" }}>
                                <RotateRightIcon />
                            </IconButton>
                        </Tooltip>

                        {/* PDF page controls (pdf.js mode only) */}
                        {isPdf && !useIframeFallback && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
                                <Tooltip title="Previous Page" {...tooltipProps}>
                                    <span>
                                        <IconButton
                                            onClick={() => setPageNumber((n) => Math.max(1, n - 1))}
                                            disabled={pageNumber <= 1}
                                            sx={{ color: "#1976d2" }}
                                        >
                                            <NavigateBeforeIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <TextField
                                    size="small"
                                    value={pageNumber}
                                    onChange={onPageInput}
                                    sx={{ width: 80 }}
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", style: { textAlign: "center" } }}
                                />
                                <Typography variant="body2" sx={{ minWidth: 64, textAlign: "center" }}>
                                    / {numPages || 1}
                                </Typography>
                                <Tooltip title="Next Page" {...tooltipProps}>
                                    <span>
                                        <IconButton
                                            onClick={() => setPageNumber((n) => Math.min((numPages || 1), n + 1))}
                                            disabled={pageNumber >= (numPages || 1)}
                                            sx={{ color: "#1976d2" }}
                                        >
                                            <NavigateNextIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip title="Email" {...tooltipProps}>
                            <IconButton onClick={handleEmail} sx={{ color: "#1976d2" }}>
                                <EmailIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Print" {...tooltipProps}>
                            <IconButton onClick={handlePrint} sx={{ color: "#1976d2" }}>
                                <PrintIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close" {...tooltipProps}>
                            <IconButton onClick={onClose} sx={{ color: "#1976d2" }}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Content */}
                <Box
                    ref={canvasWrapRef}
                    sx={{
                        position: "relative",
                        borderRadius: 1,
                        border: "1px solid rgba(0,0,0,0.12)",
                        overflow: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 1,
                        flex: 1,
                        transform: `rotate(${rotation}deg)`,
                        transformOrigin: "center center",
                    }}
                >
                    {/* Image mode */}
                    {!isPdf && (
                        <img
                            src={src}
                            alt="Media Content"
                            style={{ maxWidth: "100%", maxHeight: "84vh", display: "block" }}
                        />
                    )}

                    {/* PDF via pdf.js */}
                    {isPdf && !useIframeFallback && (
                        <>
                            {loadingPdf && (
                                <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            {!!pdfError && (
                                <Typography color="error" sx={{ position: "absolute", top: 8, left: 8 }}>
                                    {pdfError}
                                </Typography>
                            )}
                            <canvas ref={canvasRef} />
                        </>
                    )}

                    {/* Fallback: native PDF viewer */}
                    {isPdf && useIframeFallback && (
                        <iframe
                            src={src}
                            title="Media PDF"
                            style={{ width: "100%", height: "84vh", border: "none" }}
                        />
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default MediaViewer;
